import {useState} from "react";
import {PhoneHQGlyph, PhoneHQConfirmDialog} from 'ch3-ui-lib';
import {useOverrideConfirmEnsAlertMutation} from "../../../store/features/ens/ensService";
import {EnsRecipient} from "../../../store/features/ens/types";

interface Props {
    publicId: string;
    recipients: EnsRecipient[];
    isTerminal: boolean;
}

export const OverrideTab = ({publicId, recipients, isTerminal}: Props) => {
    const [overrideTarget, setOverrideTarget] = useState<EnsRecipient | null>(null);
    const [overrideConfirm] = useOverrideConfirmEnsAlertMutation();

    const pending = recipients.filter((r) => r.status === 'PENDING');

    if (isTerminal) {
        return (
            <div className="px-5 py-10 text-center text-sm text-ink-muted">
                Alert is no longer active. Override is unavailable.
            </div>
        );
    }

    if (pending.length === 0) {
        return (
            <div className="px-5 py-10 text-center text-sm text-ink-muted">
                No recipients awaiting override.
            </div>
        );
    }

    const requestOverride = (recipient: EnsRecipient) => {
        setOverrideTarget(recipient);
    };

    const handleOverride = async (confirmed: boolean) => {
        if (!confirmed || !overrideTarget) return;
        try {
            await overrideConfirm({publicId, userId: overrideTarget.userId}).unwrap();
        } catch {
            // toast handled in service
        } finally {
            setOverrideTarget(null);
        }
    };

    return (
        <div className="flex flex-col gap-2 px-4 py-3">
            <div className="mb-1 rounded-lg border border-brand-tint bg-brand-tint-subtle px-3 py-2.5">
                <div className="text-xs font-semibold text-brand-ink">Manual override</div>
                <div className="mt-0.5 text-[11px] leading-snug text-brand-ink">
                    Mark a recipient as confirmed when they have acknowledged through an external channel
                    (Slack, in-person, etc.).
                </div>
            </div>
            {pending.map((recipient) => (
                <div
                    key={recipient.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-line bg-surface-muted px-2.5 py-2"
                >
                    <div className="min-w-0">
                        <div className="truncate text-xs font-semibold text-ink">User #{recipient.userId}</div>
                        <div className="text-[10px] text-ink-muted">
                            at L{recipient.currentLevelSequence ?? '–'}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => requestOverride(recipient)}
                        className="flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 text-[11px] font-semibold text-ink-secondary transition-colors hover:border-success-tint hover:text-success-ink"
                    >
                        <PhoneHQGlyph name="check" size={14} className="text-success-ink"/>
                        Mark confirmed
                    </button>
                </div>
            ))}
            <PhoneHQConfirmDialog
                open={overrideTarget !== null}
                onOpenChange={(open) => !open && setOverrideTarget(null)}
                title="Override-confirm recipient"
                cancelLabel="Cancel"
                confirmLabel="Confirm on their behalf"
                onConfirm={() => void handleOverride(true)}
            >
                {overrideTarget ? `Marking user #${overrideTarget.userId} as confirmed by admin override.` : ''}
            </PhoneHQConfirmDialog>
        </div>
    );
};
